import React from 'react'
import { api } from '../lib/api.js'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const FALLBACK_ITEMS = [
  {
    date: 'Pre-Alpha',
    title: 'Core gunplay + movement',
    body: 'ADS tuning, recoil patterns, slide/cancel rules, and first-pass netcode for small lobbies.'
  },
  {
    date: 'Alpha',
    title: 'Battle royale loop',
    body: 'Drop, loot, contract, rotate. Plates, inventory, buy stations, and a working endgame circle model.'
  },
  {
    date: 'Beta',
    title: 'Ranked + anti-cheat hardening',
    body: 'Telemetry pipeline, server authoritative checks, and ban/appeal tooling to support long-running seasons.'
  },
  {
    date: 'Launch',
    title: 'Season 0',
    body: 'Stable patch delivery via the launcher, onboarding, first playlist rotation, and creator-ready press kit.'
  }
]

export default function Milestones() {
  const rootRef = React.useRef(null)
  const [items, setItems] = React.useState(FALLBACK_ITEMS)

  React.useEffect(() => {
    let live = true
    ;(async () => {
      try {
        const data = await api('/api/milestones')
        if (live && Array.isArray(data.items)) setItems(data.items)
      } catch {
        // keep fallback
      }
    })()
    return () => { live = false }
  }, [])

  React.useEffect(() => {
    const root = rootRef.current
    if (!root) return

    const cards = Array.from(root.querySelectorAll('[data-card]'))

    cards.forEach((card) => {
      gsap.fromTo(
        card,
        { y: 24, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.7,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: card,
            start: 'top 80%',
            end: 'top 50%',
            toggleActions: 'play none none reverse'
          }
        }
      )
    })

    const line = root.querySelector('[data-line]')
    if (line) {
      gsap.fromTo(
        line,
        { scaleY: 0 },
        {
          scaleY: 1,
          transformOrigin: 'top',
          scrollTrigger: {
            trigger: root,
            start: 'top 70%',
            end: 'bottom 70%',
            scrub: true
          }
        }
      )
    }

    return () => {
      ScrollTrigger.getAll().forEach((st) => st.kill())
    }
  }, [])

  return (
    <section id="milestones" ref={rootRef} className="mt-20">
      <div className="flex items-end justify-between gap-6">
        <div>
          <div className="text-xs tracking-[0.32em] text-white/60">PROJECT</div>
          <h2 className="mt-2 text-2xl md:text-4xl font-semibold">Milestones</h2>
          <p className="mt-3 text-white/60 max-w-2xl">A scroll-driven timeline with a progress line and reveal animations, similar in feel to the premium milestone section you linked.</p>
        </div>
        <a href="/news" className="hidden md:inline-flex rounded-lg border border-white/15 bg-white/10 px-3 py-2 text-sm hover:bg-white/15">View updates</a>
      </div>

      <div className="mt-10 relative">
        <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-white/10" />
        <div data-line className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-white/50 opacity-40" style={{ transform: 'scaleY(0)' }} />

        <div className="space-y-6">
          {items.map((it, idx) => (
            <div key={it.title} className="relative">
              <div className="absolute left-4 md:left-1/2 top-6 -translate-x-1/2 h-3 w-3 rounded-full bg-white" />

              <div
                data-card
                className={`rounded-2xl border border-white/10 bg-panel/60 glow p-5 md:p-7 w-full md:w-[calc(50%-2rem)] ${idx % 2 === 0 ? 'md:ml-auto' : 'md:mr-auto'}`}
              >
                <div className="text-xs tracking-[0.28em] text-white/50">{it.date}</div>
                <div className="mt-2 text-xl font-semibold">{it.title}</div>
                <div className="mt-2 text-white/60">{it.body}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
